import type { InputEncoder, Outgoing } from './input-encoder.js';
import type { PointerIntent } from './types.js';

/**
 * Client-side coalescing per §6.6:
 *  - hover move / drag: latest-wins at <=120 Hz (move) / <=60 Hz (drag)
 *  - wheel: <=60 Hz, deltas SUMMED (never drop scroll distance)
 * Discrete events (down/up/key/text) bypass this and send immediately.
 *
 * The caller drives tick() from a rAF/timer; the coalescer emits at most one move-or-drag
 * and one wheel per tick. DOM-free: it takes already-normalized PointerIntents.
 */
export class Coalescer {
  private pendingMove: PointerIntent | null = null;
  private pendingWheel: { p: PointerIntent; dx: number; dy: number; mods: number } | null = null;
  private lastMoveFlush = -Infinity;
  private lastWheelFlush = -Infinity;

  constructor(
    private encoder: InputEncoder,
    private emit: (o: Outgoing) => void,
    private opts: { moveHz?: number; dragHz?: number; wheelHz?: number } = {},
  ) {}

  sampleMove(p: PointerIntent): void {
    this.pendingMove = p; // latest-wins
  }

  sampleWheel(p: PointerIntent, dx: number, dy: number, mods: number): void {
    if (this.pendingWheel && this.pendingWheel.p.disp === p.disp) {
      this.pendingWheel.dx += dx;
      this.pendingWheel.dy += dy;
      this.pendingWheel.p = p;
      this.pendingWheel.mods = mods;
    } else {
      this.pendingWheel = { p, dx, dy, mods };
    }
  }

  /** Flush any pending samples that are due at time `now` (ms). */
  tick(now: number): void {
    if (this.pendingMove) {
      // drag when a button is held (60 Hz), else hover (120 Hz)
      const hz = this.encoder.anyButtonHeld ? this.opts.dragHz ?? 60 : this.opts.moveHz ?? 120;
      if (now - this.lastMoveFlush >= 1000 / hz) {
        this.emit(this.encoder.pointerMove(this.pendingMove));
        this.pendingMove = null;
        this.lastMoveFlush = now;
      }
    }
    if (this.pendingWheel) {
      const hz = this.opts.wheelHz ?? 60;
      if (now - this.lastWheelFlush >= 1000 / hz) {
        const w = this.pendingWheel;
        this.emit(this.encoder.wheel(w.p, w.dx, w.dy, w.mods));
        this.pendingWheel = null;
        this.lastWheelFlush = now;
      }
    }
  }

  /** Drop any pending samples (on reset/blur) without sending. */
  clear(): void {
    this.pendingMove = null;
    this.pendingWheel = null;
  }
}
